import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import Modal from "@/components/base/Modal";
import { useApi } from "@/hooks/useApi";
import { apiPost } from "@/lib/api";
import type { FreeComment, FreePost } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import Avatar from "@/components/base/Avatar";
import Lightbox from "@/components/base/Lightbox";
import { useAuth } from "@/components/feature/AuthProvider";
import { useToast } from "@/components/feature/ToastProvider";
import { mediaUrl } from "@/lib/media";

function formatDate(value?: string): string {
  if (!value) return "";
  const str = String(value).replace(" ", "T");
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return value;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}.${m}.${day} ${hh}:${mm}`;
}

interface LikeState {
  is_liked: number;
  like_cnt: number;
}

export default function FreeDetailPage() {
  const { no } = useParams();
  const navigate = useNavigate();
  const { member, requireLogin } = useAuth();
  const { toast } = useToast();

  const { data, loading, error, reload } = useApi<FreePost>(
    no ? `frees.php?no=${no}` : null
  );

  const [lightbox, setLightbox] = useState<number | null>(null);
  const [likeState, setLikeState] = useState<LikeState | null>(null);
  const [likeBusy, setLikeBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [deletePostOpen, setDeletePostOpen] = useState(false);
  const [deleteCommentNo, setDeleteCommentNo] = useState<number | null>(null);

  const post = data;
  const images = useMemo(() => post?.images ?? [], [post]);
  const comments = useMemo(() => post?.comments ?? [], [post]);
  const w = post?.writer;
  const writerName =
    [w?.name, w?.aho].filter(Boolean).join(" ") ||
    [post?.name, post?.aho].filter(Boolean).join(" ");

  const isLiked = likeState?.is_liked ?? post?.is_liked ?? 0;
  const likeCnt = likeState?.like_cnt ?? post?.like_cnt ?? 0;

  // 새 글 로딩 시 낙관적 상태 초기화
  useEffect(() => {
    setLikeState(null);
  }, [no]);

  const toggleLike = async () => {
    if (!requireLogin()) return;
    if (likeBusy || !post) return;
    setLikeBusy(true);

    const prev: LikeState = { is_liked: isLiked ? 0 : 1, like_cnt: Math.max(0, likeCnt + (isLiked ? -1 : 1)) };
    setLikeState(prev); // 낙관적 UI
    try {
      const res = await apiPost<{ no: number; is_liked: number; like_cnt: number }>(
        "free_write.php",
        { act: "like", no: post.no }
      );
      const d = res.data;
      if (d && typeof d.like_cnt === "number") {
        setLikeState({ is_liked: d.is_liked ?? prev.is_liked, like_cnt: d.like_cnt });
      }
    } catch (e) {
      setLikeState({ is_liked: post.is_liked ?? 0, like_cnt: post.like_cnt ?? 0 });
      toast(e instanceof Error ? e.message : "처리하지 못했습니다.", "error");
    } finally {
      setLikeBusy(false);
    }
  };

  const submitComment = async () => {
    if (!requireLogin()) return;
    const content = comment.trim();
    if (!content) {
      toast("댓글 내용을 입력해 주세요.", "info");
      return;
    }
    if (content.length > 500) {
      toast("댓글은 500자 이내로 입력해 주세요.", "info");
      return;
    }
    setSending(true);
    try {
      await apiPost("free_write.php", { act: "comment", no: post?.no, content });
      setComment("");
      reload();
      toast("댓글을 등록했습니다.", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "등록에 실패했습니다.", "error");
    } finally {
      setSending(false);
    }
  };

  const deletePost = async () => {
    if (!post) return;
    try {
      await apiPost("free_write.php", { act: "delete", no: post.no });
      toast("삭제되었습니다.", "success");
      navigate("/free");
    } catch (e) {
      toast(e instanceof Error ? e.message : "삭제에 실패했습니다.", "error");
      setDeletePostOpen(false);
    }
  };

  const deleteComment = async () => {
    if (deleteCommentNo == null) return;
    try {
      await apiPost("free_write.php", { act: "comment_del", no: deleteCommentNo });
      setDeleteCommentNo(null);
      reload();
      toast("댓글을 삭제했습니다.", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "삭제에 실패했습니다.", "error");
    }
  };

  if (loading) {
    return (
      <>
        <PageBanner title="자유글" breadcrumb={[{ label: "자유글", to: "/free" }, { label: "글 보기" }]} />
        <div className="mx-auto max-w-3xl px-4 md:px-6 py-10 space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-40 w-full" />
        </div>
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <PageBanner title="자유글" breadcrumb={[{ label: "자유글", to: "/free" }, { label: "글 보기" }]} />
        <section className="mx-auto max-w-3xl px-4 md:px-6 py-12">
          <EmptyState
            icon="ri-error-warning-line"
            title="글을 불러오지 못했습니다"
            description={error ?? "존재하지 않는 글입니다."}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </section>
      </>
    );
  }

  return (
    <>
      <PageBanner
        title="자유글"
        breadcrumb={[{ label: "자유글", to: "/free" }, { label: "글 보기" }]}
      />

      <article className="mx-auto max-w-3xl px-4 md:px-6 py-8 md:py-10">
        <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-7">
          <div className="flex items-center gap-3">
            <Avatar name={writerName || "회원"} photo={w?.photo || post.photo} size={44} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground-800">
                {writerName || "회원"}
              </p>
              <p className="text-xs text-foreground-400">
                {(w?.biz_name || post.biz_name) ? `${w?.biz_name || post.biz_name} · ` : ""}
                {formatDate(post.regdate || post.reg_date)}
              </p>
            </div>
            {post.is_mine === 1 && (
              <button
                type="button"
                onClick={() => setDeletePostOpen(true)}
                className="ml-auto shrink-0 whitespace-nowrap rounded-full border border-accent-200 bg-accent-50 px-4 py-2 text-xs font-semibold text-accent-800 hover:bg-accent-100 transition-colors"
              >
                <i className="ri-delete-bin-line mr-1" aria-hidden="true"></i>
                삭제
              </button>
            )}
          </div>

          <h1 className="mt-5 font-heading text-xl md:text-2xl font-bold text-foreground-950">
            {post.subject || "제목 없음"}
          </h1>

          <div className="mt-5 whitespace-pre-wrap text-sm md:text-base leading-relaxed text-foreground-800">
            {post.content}
          </div>

          {images.length > 0 && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {images.map((img, i) => (
                <button
                  key={`${img}-${i}`}
                  type="button"
                  onClick={() => setLightbox(i)}
                  className="group overflow-hidden rounded-lg bg-background-100"
                >
                  <div className="h-32 w-full overflow-hidden">
                    <img
                      src={mediaUrl(img)}
                      alt={`첨부 이미지 ${i + 1}`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 flex items-center gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={toggleLike}
              disabled={likeBusy}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-70 ${
                isLiked
                  ? "bg-accent-500 text-primary-950"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i
                className={isLiked ? "ri-heart-3-fill" : "ri-heart-3-line"}
                aria-hidden="true"
              ></i>
              공감 {likeCnt}
            </button>
            <span className="inline-flex items-center gap-2 text-sm text-foreground-500">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              댓글 {comments.length}
            </span>
          </div>
        </div>

        {/* 댓글 */}
        <section className="mt-6">
          <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">
            댓글 {comments.length}
          </h2>

          <div className="space-y-3">
            {comments.length === 0 ? (
              <p className="rounded-xl border border-dashed border-background-300 bg-background-50 py-8 text-center text-sm text-foreground-400">
                첫 번째 댓글을 남겨보세요.
              </p>
            ) : (
              comments.map((c: FreeComment) => {
                const cw = c.writer;
                const cName =
                  [cw?.name, cw?.aho].filter(Boolean).join(" ") ||
                  [c.name, c.aho].filter(Boolean).join(" ") ||
                  "회원";
                return (
                  <div
                    key={c.no}
                    className="flex items-start gap-3 rounded-xl border border-background-200 bg-background-50 p-4"
                  >
                    <Avatar name={cName} photo={cw?.photo || c.photo} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground-800">{cName}</p>
                        <span className="text-xs text-foreground-400">
                          {formatDate(c.regdate || c.reg_date)}
                        </span>
                        {c.is_mine === 1 && (
                          <button
                            type="button"
                            onClick={() => setDeleteCommentNo(c.no)}
                            aria-label="댓글 삭제"
                            className="ml-auto w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:bg-background-100 hover:text-accent-700"
                          >
                            <i className="ri-delete-bin-line text-sm" aria-hidden="true"></i>
                          </button>
                        )}
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-foreground-700">
                        {c.content}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-5">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submitComment();
                  }
                }}
                placeholder={
                  member ? "댓글을 입력하세요" : "로그인 후 댓글 작성이 가능합니다"
                }
                disabled={!member}
                maxLength={500}
                className="flex-1 rounded-full border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
              <button
                type="button"
                onClick={submitComment}
                disabled={sending || !member}
                className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                등록
              </button>
            </div>
          </div>
        </section>

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/free")}
            className="whitespace-nowrap inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            목록으로
          </button>
        </div>
      </article>

      {lightbox !== null && (
        <Lightbox
          images={images}
          index={lightbox}
          onIndexChange={setLightbox}
          onClose={() => setLightbox(null)}
          alt="첨부 이미지"
        />
      )}

      <Modal open={deletePostOpen} onClose={() => setDeletePostOpen(false)}>
        <div className="p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">
            글을 삭제할까요?
          </h2>
          <p className="mt-2 text-sm text-foreground-600">삭제한 글은 되돌릴 수 없습니다.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={deletePost}
              className="flex-1 whitespace-nowrap rounded-md bg-accent-600 px-5 py-3 text-sm font-bold text-background-50 hover:bg-accent-700 transition-colors"
            >
              삭제
            </button>
            <button
              type="button"
              onClick={() => setDeletePostOpen(false)}
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
            >
              취소
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={deleteCommentNo != null} onClose={() => setDeleteCommentNo(null)}>
        <div className="p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">
            댓글을 삭제할까요?
          </h2>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={deleteComment}
              className="flex-1 whitespace-nowrap rounded-md bg-accent-600 px-5 py-3 text-sm font-bold text-background-50 hover:bg-accent-700 transition-colors"
            >
              삭제
            </button>
            <button
              type="button"
              onClick={() => setDeleteCommentNo(null)}
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
            >
              취소
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}